import { useMemo, useSyncExternalStore } from 'react';
import { maxDiscountPct, normalizePosSettings, type PosSettings } from '@pos/core';
import { useSession } from '../../app/current.tsx';
import { EMPTY_CART, type Cart } from './cart.ts';

/** Configuración del punto de venta del negocio (con valores por defecto). */
export function usePosSettings(): PosSettings {
  const { business } = useSession();
  const raw = business.settings?.pos;
  return useMemo(() => normalizePosSettings(raw), [raw]);
}

/** Tope de descuento del usuario actual, en % (100 para el dueño). */
export function useMaxDiscount(): number {
  const { membership } = useSession();
  return maxDiscountPct({ role: membership.role, permissions: membership.permissions, active: membership.active });
}

// ---------------------------------------------------------------------------
// Carrito: sobrevive a cambiar de pantalla y a recargar la página (se guarda en este
// navegador, por negocio y usuario). Si el almacenamiento no está disponible, vive en memoria.
// ---------------------------------------------------------------------------

const carts = new Map<string, Cart>();
const listeners = new Set<() => void>();
const storageKey = (key: string) => `pos-saas-cart:${key}`;

function read(key: string): Cart {
  const cached = carts.get(key);
  if (cached) return cached;
  let cart = EMPTY_CART;
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (raw) {
      const parsed = JSON.parse(raw) as Cart;
      if (Array.isArray(parsed.lines) && typeof parsed.globalDiscount === 'number') cart = parsed;
    }
  } catch {
    // sin almacenamiento o dato corrupto: carrito vacío
  }
  carts.set(key, cart);
  return cart;
}

function write(key: string, cart: Cart): void {
  carts.set(key, cart);
  try {
    if (cart.lines.length === 0) localStorage.removeItem(storageKey(key));
    else localStorage.setItem(storageKey(key), JSON.stringify(cart));
  } catch {
    // queda en memoria
  }
  listeners.forEach((l) => l());
}

export function useCart(): [Cart, (update: Cart | ((cart: Cart) => Cart)) => void] {
  const { business, membership } = useSession();
  const key = `${business.id}:${membership.user_id}`;
  const cart = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => read(key),
  );
  const setCart = useMemo(
    () => (update: Cart | ((cart: Cart) => Cart)) => write(key, typeof update === 'function' ? update(read(key)) : update),
    [key],
  );
  return [cart, setCart];
}
